import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgControl,
  NgForm
} from '@angular/forms';
import { ErrorStateMatcher, mixinErrorState } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  startWith,
  Subject,
  switchMap
} from 'rxjs';
import { AtomService } from 'src/app/core/atom.service';
import { Country } from 'src/app/core/resources/country.resource';
import { Lang } from 'src/app/core/resources/lang.resource';

export interface Translation {
  lang: string | null;
  country: string | null;
  value: string | null;
}

class TranslationInputBase {
  readonly stateChanges = new Subject<void>();

  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    public ngControl: NgControl
  ) {}
}

const TranslationInputMixinBase = mixinErrorState(TranslationInputBase);

@Component({
  selector: 'atm-translation-input',
  templateUrl: './translation-input.component.html',
  styleUrls: ['./translation-input.component.scss'],
  providers: [
    { provide: MatFormFieldControl, useExisting: TranslationInputComponent },
  ],
})
export class TranslationInputComponent
  extends TranslationInputMixinBase
  implements
    OnInit,
    DoCheck,
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<Translation>
{
  static nextId = 0;

  translation = new FormGroup({
    lang: new FormControl(),
    country: new FormControl(),
    value: new FormControl(),
  });

  filteredCountries?: Observable<Country[]>;
  filteredLangs?: Observable<Lang[]>;

  isRTL$?: Observable<boolean>;

  @HostBinding()
  id = `countries-input-${TranslationInputComponent.nextId++}`;

  @ViewChild('input', { static: true })
  input?: ElementRef;

  focused = false;
  controlType = 'countries-input';

  @Input()
  type: 'text' | 'textarea' | undefined;

  @Input()
  get value(): Translation | null {
    const { lang, country, value } = this.translation.value;
    if (lang && country && value) {
      return { lang, country, value };
    }
    return null;
  }
  set value(translation: Translation | null) {
    const { lang, country, value } = translation || {
      lang: null,
      country: null,
      value: null,
    };
    this.translation.setValue({ lang, country, value });
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this.placeholderText;
  }
  set placeholder(plh) {
    this.placeholderText = plh;
    this.stateChanges.next();
  }
  private placeholderText = '';

  get empty(): boolean {
    const { lang, country, value } = this.translation.value;
    return !lang && !country && !value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input()
  get required(): boolean {
    return this.isRequired;
  }
  set required(required: boolean) {
    this.isRequired = coerceBooleanProperty(required);
    this.stateChanges.next();
  }
  private isRequired = false;

  @Input()
  set disabled(disabled) {
    coerceBooleanProperty(disabled)
      ? this.translation.disable()
      : this.translation.enable();
    this.stateChanges.next();
  }
  get disabled(): boolean {
    return this.translation.disabled;
  }

  @HostBinding('attr.aria-describedby')
  describedBy = '';

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    const tagName = (event.target as Element).tagName.toLowerCase();
    if (tagName !== 'input' && tagName !== 'textarea') {
      this.elRef.nativeElement.querySelector('input')!.focus();
    }
  }

  onChange = () => {};
  onTouched = () => {};

  constructor(
    private atom: AtomService,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Inject(LOCALE_ID) public readonly locale: string,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    @Optional() @Self() ngControl: NgControl
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    fm.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    const langControl = this.translation.get('lang')!;
    const countryControl = this.translation.get('country')!;
    const langOrCountryChange = merge(
      langControl.valueChanges,
      countryControl.valueChanges
    ).pipe(
      distinctUntilChanged(),
      debounceTime(500),
      map(() => ({ lang: langControl.value, country: countryControl.value }))
    );

    this.filteredCountries = langOrCountryChange.pipe(
      switchMap(({ lang, country }) => this.filterCountries(country, lang))
    );
    this.filteredLangs = langOrCountryChange.pipe(
      switchMap(({ lang, country }) => this.filterLangs(lang, country))
    );
    this.isRTL$ = langControl.valueChanges.pipe(
      startWith(langControl.value),
      map((lang) => lang?.trim().toLowerCase() === 'ar')
    );
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  writeValue(value: Translation | null): void {
    this.value = value;
  }

  registerOnChange(onChange: (_: Translation | null) => void): void {
    this.onChange = () => {
      onChange(this.value);
    };
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private filterCountries(
    id: string | undefined,
    langId: string | undefined
  ): Observable<Country[]> {
    return this.atom.list(Country, {
      filter: {
        'like.id': id && id.length > 0 ? id : undefined,
        'like.langs.id': langId && langId.length > 0 ? langId : undefined,
      },
    });
  }

  private filterLangs(
    id: string | undefined,
    countryId: string | undefined
  ): Observable<Lang[]> {
    return this.atom.list(Lang, {
      filter: {
        'like.id': id && id.length > 0 ? id : undefined,
        'like.countries.id':
          countryId && countryId.length > 0 ? countryId : undefined,
      },
    });
  }
}
